(ns io.daskul.webapp.widgets.record.alerts
  "F-059 record-level alerts, rendered from `[:_meta :alerts]`.

   Placement: below the tab strip, inside Details, ABOVE the pinned facts —
   and it SCROLLS AWAY. Once you have read it, it should stop taking space;
   the state chip and the toned pinned fact are the persistent record. So this
   is deliberately not sticky.

   Never use this for a blocked action. That is a guard, it concerns ONE
   action, and it belongs under that action's own button (#696) — nothing is
   wrong with the record."
  (:require
   [uix.core :as uix :refer [defui $]]))

;; NO `io.daskul.webapp.ui` REQUIRE. It transitively imports the tabler icon
;; set, and the :node-test build cannot resolve those modules — so requiring
;; it here breaks the whole cljs suite, not just this namespace's tests.
;; `pinned-facts` set the same precedent: a record widget draws plain divs
;; from tokens, which is also why these components need no Mantine theme to
;; be correct.

(def max-banners
  "A framework constant, not a convention (F-059). Four stacked banners is a
   wall nobody reads, and in an admin tool that is the realistic case — so
   past two they collapse behind a disclosure rather than degrading."
  2)

(def severity->tone
  "NO `success`. A settled record is shown by its chip, not announced by a
   banner — a banner that says everything is fine is a banner nobody reads
   the next time. Action outcomes are transient and belong to the click
   rather than to the record: those are toasts.

   The test for banner-vs-toast is whether it is still true on reload."
  {:critical {:bg "var(--critical-bg)" :border "var(--critical-border)"
              :title "var(--critical-strong)" :body "var(--critical-body)"
              :fg "var(--critical-fg)" :glyph "⚠"}
   :warning  {:bg "var(--warning-bg)" :border "var(--warning-border)"
              :title "var(--warning-strong)" :body "var(--warning-body)"
              :fg "var(--warning-fg)" :glyph "⏱"}
   :info     {:bg "var(--info-bg)" :border "var(--info-border)"
              :title "var(--info-strong)" :body "var(--info-body)"
              :fg "var(--info-fg)" :glyph "ℹ"}
   ;; A standing statement with nothing to do about it. NOT `success`: that
   ;; word belongs to toasts, where an outcome is transient and attached to a
   ;; click. This is a permanent fact about the record, so it is neutral, it
   ;; sorts last, and it does not render as a banner at all — see `banner?`.
   :note     {:bg "var(--neutral-bg)" :border "var(--border)"
              :title "var(--text)" :body "var(--text-muted)"
              :fg "var(--text-muted)" :glyph "•"}})

(def ^:private severity-rank
  {:critical 0 :warning 1 :info 2 :note 3})

(defn banner?
  "Does this alert earn a banner?

   `:note` does not. It is a standing statement — \"This enrollment has been
   marked as attended.\" — on a record whose chip already says so, and a
   banner for it would be louder than the fact it reports. Notes render as
   the quiet card in the rail, which is exactly where they rendered before
   they became alerts."
  [alert]
  (not= :note (some-> (:severity alert) keyword)))

(defn tone
  "Colours for a severity. An unrecognised or missing severity degrades to
   `warning` rather than throwing or rendering unstyled — a typo in a
   definition should not produce an invisible banner."
  [severity]
  (or (get severity->tone (some-> severity keyword))
      (:warning severity->tone)))

(defn sort-by-severity
  "Critical first, then warning, then info; declaration order breaks ties.

   The SERVER returns declaration order deliberately (#701) so this ordering,
   the cap and the disclosure are all applied in one place — if the server
   sorted too, the list-row dot and the banner could disagree about which
   condition is the record's headline."
  [alerts]
  (vec (sort-by #(get severity-rank (some-> (:severity %) keyword) 1)
                (vec alerts))))

(defn split-for-display
  "`{:shown [...] :hidden n}` — at most `max-banners` shown.

   Returns the count rather than the hidden alerts because the disclosure
   only ever says how many; expanding re-renders with the full set."
  [alerts]
  (let [sorted (sort-by-severity alerts)]
    {:shown (vec (take max-banners sorted))
     :hidden (max 0 (- (count sorted) max-banners))}))

(defn dot-severity
  "The severity a list row's dot carries: the most severe alert on the record,
   ignoring `:note`.

   nil when there is nothing to mark, which is the common case — most rows in
   a healthy list get no dot at all, and that is what makes the ones that do
   worth looking at.

   `:note` is excluded on the same grounds it never banners: it is a standing
   remark about a settled record, and a list where every enrolled student
   carries a mark is a list with no signal in it.

   Reads the same `severity-rank` the banners sort by, so a row's dot and the
   headline banner on the record it opens can never name different
   conditions — which is the whole claim F-062 makes for one declaration
   driving several surfaces."
  [record]
  (when-let [worst (->> (get-in record [:_meta :alerts])
                        (filter banner?)
                        sort-by-severity
                        first)]
    ;; Degrade a missing severity to :warning, exactly as `tone` and
    ;; `severity-rank` do. Returning nil here instead would give a record that
    ;; banners on its detail page an unmarked row — the one failure mode you
    ;; could not see, and precisely the disagreement between surfaces that
    ;; F-062 exists to rule out.
    (or (some-> (:severity worst) keyword) :warning)))

(defn link->nav
  "Normalize an alert/fact `:link` into a navigation intent, or nil for a
   display-only link (no `:href`, no `:route`) — a phone number shows itself
   and goes nowhere.

   Pure: the caller performs the effect. A `:route` link's path `:params` and
   `:query-params` are stringified — they arrive as keywords/uuids and the
   router wants strings. `:query-params` carries a create-page's prefill
   (#850): {:route :aggregate/new :params {:agg \"invoices\"}
            :query-params {:customer-type … :customer-id …}} →
   /a/invoices/new?customer-type=…&customer-id=…

   `:route` is keywordised, so it round-trips whether it arrives as a keyword
   or (through JSON) as a string."
  [{:keys [href route params query-params]}]
  (let [strs (fn [m] (into {} (map (fn [[k v]] [k (str v)])) m))]
    (cond
      href  {:href href}
      route {:route (keyword route)
             :params (strs params)
             :query-params (strs query-params)})))

(defui alert-dot
  "The list-row marker. A dot, not a tint or a stripe: it has to survive a
   striped table and a hover highlight, and it has to mean the same thing at
   375px as at 1280px.

   `title` rather than a tooltip component — this sits in a table cell that is
   already dense, and the alert's own text is the honest label. Screen readers
   get it for free."
  [{:keys [severity label]}]
  (when severity
    ($ :span {:title label
              :aria-label label
              :role "img"
              :style {:display "inline-block"
                      :width "8px"
                      :height "8px"
                      :borderRadius "50%"
                      :background (:fg (tone severity))}})))

(defui alert-banner
  "One condition. Buttons are WHITE-ON-TINT, never filled — amber never fills
   a button, and a filled button here would outrank the header's primary
   action while saying something the record merely wants you to know.

   Actions sit IN THE CONTENT FLOW (#787): left-aligned under the body,
   indented to the text column — the inline-alert convention, icon → title
   → body → act. Flanking the text column stole body width (three buttons
   forced a wrap); sharing the title row read as a toolbar with a caption;
   bottom-right read as a dialog footer on a surface that isn't a dialog."
  [{:keys [severity title body link actions on-action on-link]}]
  (let [t (tone severity)
        ;; White-on-tint, shared by the link CTA and the action buttons — a
        ;; filled button here would outrank the header's primary action.
        btn-style {:height "28px" :padding "0 12px"
                   :borderRadius "8px"
                   :background "var(--surface-card)"
                   :border (str "1px solid " (:border t))
                   :color (:fg t)
                   :fontFamily "var(--font-body)"
                   :fontSize "13px" :fontWeight 600
                   :cursor "pointer"
                   :whiteSpace "nowrap"}
        acts? (or link (seq actions))]
    ($ :div {:data-testid "record-alert"
             :data-severity (some-> severity name)
             :style {:background (:bg t)
                     :border (str "1px solid " (:border t))
                     :borderRadius "var(--mantine-radius-md)"
                     :padding (if body "14px 18px" "13px 18px")
                     :display "flex"
                     :alignItems (if (or body acts?) "flex-start" "center")
                     :gap "13px"}}
      ($ :span {:style {:fontSize "15px" :color (:fg t) :lineHeight 1.3}}
        (:glyph t))
      ($ :div {:style {:flex 1 :minWidth 0}}
        ($ :div {:style {:fontSize "14px" :fontWeight 700 :color (:title t)}}
          title)
        (when body
          ($ :div {:style {:fontSize "13px" :lineHeight 1.55
                           :color (:body t) :marginTop "3px"}}
            body))
        (when acts?
          ($ :div {:style {:display "flex" :gap "9px" :flexWrap "wrap"
                           :marginTop "10px"}}
            ;; The link — where the work happens (#850 Create Invoice) — leads,
            ;; before any state-changing action.
            (when link
              ($ :button {:type "button"
                          :data-testid "alert-link"
                          :onClick #(when on-link (on-link link))
                          :style btn-style}
                (:label link)))
            (for [{:keys [id label]} actions]
              ($ :button {:key (str id)
                          :type "button"
                          :onClick #(when on-action (on-action id))
                          :style btn-style}
                label))))))))

(defui alert-stack
  "At most two banners; the rest collapse behind a disclosure.

   Renders nothing when there are no alerts — an empty container here would
   reserve space above the facts card on every record that has nothing wrong
   with it, which is most of them."
  [{:keys [alerts on-action on-link]}]
  (let [[expanded? set-expanded!] (uix/use-state false)
        {:keys [shown hidden]} (split-for-display alerts)
        visible (if expanded? (sort-by-severity alerts) shown)]
    (when (seq visible)
      ($ :div {:data-testid "record-alerts"
               :style {:display "flex" :flexDirection "column"
                       :gap "var(--mantine-spacing-sm)"
                       :marginBottom "var(--mantine-spacing-md)"}}
        (for [a visible]
          ($ alert-banner (assoc a :key (str (:id a))
                                 :on-action on-action :on-link on-link)))
        (when (and (pos? hidden) (not expanded?))
          ($ :button {:type "button"
                      :onClick #(set-expanded! true)
                      :style {:alignSelf "flex-start" :border "none"
                              :background "none" :padding 0 :cursor "pointer"
                              :fontFamily "var(--font-body)" :fontSize "13px"
                              :fontWeight 600 :color "var(--text-muted)"}}
            (str hidden " more thing" (when (> hidden 1) "s")
                 (if (= 1 hidden) " needs" " need") " attention ▾")))))))
